import {
  type IAdvertisement,
  invalidatePreviousSubmissions,
  useUnderwritingQuestions,
  useUpdateSubmission,
} from 'api/submission';
import { AxiosError } from 'axios';
import {
  EMAIL_REGX,
  FULLSTORY_EVENTS,
  genericErrorMessage,
  GOOGLE_TAG_EVENTS,
} from 'common/constants';
import { displayBackendErrorMessage } from 'components/DisplayBackEndErrorMessage';
import NewQuoteWarningModal from 'components/NewQuoteWarningModal';
import { displayToastMessage } from 'components/ToastMessage';
import { useFormik } from 'formik';
import { identifyFullStory, pushToGoogleDataLayer, trackEventFullStory } from 'helpers/utils';
import { some } from 'lodash-es';
import { type FC, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { EligibilityStepPropsBase } from 'routes/Eligibility/Eligibility.types';
import { useFullscreenLoader } from 'store/features/UI';
import * as yup from 'yup';
import { useQueryParams } from '../../../../hooks/useSearchQueryParams';
import { EligibilityResultWrapper } from '../../components/EligibilityResultWrapper';
import type { EligibilitySuccessFormValues } from './EligibilitySuccess.types';

const validationSchema = yup.object({
  email: yup.string().matches(EMAIL_REGX, 'Invalid email format').required(),
});

const EligibilitySuccess: FC<EligibilityStepPropsBase> = () => {
  const [openModal, setOpenModal] = useState(false);
  const { id: submissionId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { mutateAsync: updateSubmission } = useUpdateSubmission(String(submissionId));
  const { utm_source, utm_medium, utm_campaign, utm_content, utm_term, utm_adgroup, gclid } =
    useQueryParams<IAdvertisement>();
  const { refetch: refetchUnderwritingQuestions } = useUnderwritingQuestions(
    String(submissionId),
    false,
  );

  const { setFullscreenLoading } = useFullscreenLoader();

  const formik = useFormik<EligibilitySuccessFormValues>({
    initialValues: {
      email: '',
      subscribeOnMailing: false,
    },
    validationSchema,
    onSubmit: async () => {
      if (formik.isValid) {
        try {
          setFullscreenLoading(true, 'Updating submission');
          const data: {
            id: string;
            email: string;
            subscribe_to_news: boolean;
            advertisement?: any;
          } = {
            id: String(submissionId),
            email: formik.values.email,
            subscribe_to_news: Boolean(formik.values.subscribeOnMailing),
          };

          if (
            some([utm_source, utm_medium, utm_campaign, utm_content, utm_term, utm_adgroup, gclid])
          ) {
            // Note: The eligibility widget on the THREE site jumps users directly to this step, so we need to check for advertising data here as well.
            data.advertisement = {
              utm_source,
              utm_medium,
              utm_campaign,
              utm_content,
              utm_term,
              utm_adgroup,
              gclid,
            };
          }

          const submission = await updateSubmission(data);

          refetchUnderwritingQuestions();

          if (submission.email) {
            identifyFullStory(submission.email);
            pushToGoogleDataLayer({
              event: GOOGLE_TAG_EVENTS.LEAD_CREATED,
              email: formik.values.email,
            });
            trackEventFullStory(GOOGLE_TAG_EVENTS.LEAD_CREATED);
            trackEventFullStory(FULLSTORY_EVENTS.BEGIN_ABOUT_YOU_SECTION);
            navigate(`?section=0&step=0`, { replace: true });
          }
        } catch (error) {
          if (error instanceof AxiosError) {
            /**
             * `409` status code returns if there is already an eligible quote for the provided email.
             * Redirect to the welcome back page if so.
             *
             * @see https://linear.app/three-insurance/issue/CON-244/new-flow-for-consumers
             */
            if (error.response?.status === 409) {
              setOpenModal(true);
              return;
            }
          }

          displayToastMessage('ERROR', genericErrorMessage);
        } finally {
          setFullscreenLoading(false);
        }
      }
    },
  });

  const handleOnClickPrimaryButton = async () => {
    try {
      setFullscreenLoading(true, 'Invalidating previous submissions');

      const response = await invalidatePreviousSubmissions(formik.values.email);

      displayToastMessage('SUCCESS', response.message);

      navigate('/', { replace: true });
    } catch (e) {
      displayBackendErrorMessage(e);
    } finally {
      setFullscreenLoading(false);
    }
  };

  const footerOptions = {
    primaryButton: {
      text: 'Continue to create new quote',
      onClickFn: handleOnClickPrimaryButton,
    },
    tertiaryButton: {
      text: 'Retrieve previous quote',
      onClickFn: () =>
        navigate(`/welcome-back?email=${encodeURIComponent(formik.values.email)}`, {
          replace: true,
        }),
    },
  };

  return (
    <>
      <NewQuoteWarningModal
        footerOptions={footerOptions}
        open={openModal}
        onClose={() => setOpenModal(false)}
      />
      <EligibilityResultWrapper
        actionButtonText="Continue"
        formik={formik}
        result="success"
        subtitle="Enter the email address where we should send your THREE quote."
        title="You're eligible for THREE!"
      />
    </>
  );
};

export default EligibilitySuccess;
